import { useEffect, useState } from 'react'

/**
 * Defer mounting of non-critical, expensive UI (canvas particle fields,
 * pointer-tracking aurora layers, etc.) until AFTER the first paint.
 *
 * Why: these decorations spin up `requestAnimationFrame` loops and
 * pointer listeners on mount, which adds main-thread work right when the
 * browser is trying to render the LCP element. Deferring them to idle
 * time keeps Total Blocking Time low and lets the hero text/portrait
 * paint first — the user can't perceive a 200ms delay on a background
 * ambience layer, but they very much feel a slow LCP.
 *
 * Returns `false` on the first render, flips to `true` once the browser
 * is idle (or after `timeout` ms as a ceiling). SSR-safe.
 */
export function useDeferredMount(timeout = 1200): boolean {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let id: number
    const ric = (
      window as unknown as {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number
      }
    ).requestIdleCallback

    if (typeof ric === 'function') {
      id = ric(() => setReady(true), { timeout })
    } else {
      id = window.setTimeout(() => setReady(true), 200)
    }

    return () => {
      const cic = (
        window as unknown as {
          cancelIdleCallback?: (handle: number) => void
        }
      ).cancelIdleCallback
      if (typeof cic === 'function') cic(id)
      else window.clearTimeout(id)
    }
  }, [timeout])

  return ready
}
